import React, { useState, useRef, useEffect, useContext } from "react";
import env from "react-dotenv";
import "../styles/style.css";
import "./ai-tasks/ai-tasks.css";
import { convertTo12HourFormat, trimWord } from "../util/helperFunctions";
import MessageComponent from "./message";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";
import { WebSocketContext, ChatContext } from "../context/contexts";
import emojiReaction from "../images/emotion.png";

import ReactionsIndicator from "./reusable/reactions-indicator";
import { groupReactions } from "../util/helperFunctions";
import emptyImage from "../images/empty.jpg";
import UserHoverPreview from "./user-hover-preview";
import MessageActionsMenu from "./reusable/message-actions-menu";
import MessageAttachments from "./message-attachments";

function MessageSender(props) {
  const WS = useContext(WebSocketContext);
  const Chat = useContext(ChatContext);
  const {
    messageDetail,
    userDetails,
    handleImageClick,
    toggleShowChatReply,
    setChatReplyData,
    toggleShowReactionOptions,
    onClickPin,
  } = props;
  const {
    id: chat_id,
    conversation_id,
    content,
    createdAt,
    is_deleted,
    message_attachment,
    reply_to_message_detail,
    user,
    is_pinned,
    thread_replies,
    message_reactions,
    message_reads,
    is_optimistic_pending,
  } = messageDetail;

  const [showSeenModal, setShowSeenModal] = useState(false);

  const groupedReaction = groupReactions(message_reactions);

  const messageContainerRef = useRef(null);

  const handleOnClickPin = () => {
    onClickPin(conversation_id, chat_id);
  };

  const handleViewReactions = () => {
    Chat.handleReactionsViewVisible(message_reactions);
  };

  const handleOnThreadreply = () => {
    Chat.handleThreadViewMsgId(chat_id);
  };
  const handleOnClickReply = () => {
    const replyData = {
      chat_id,
      conversation_id,
      display_name: user.display_name,
      message: content,
    };

    setChatReplyData(replyData);
  };

  const handleDelete = async (messageId) => {
    try {
      const deleteMsg = await ApiService.message.deleteMessage(
        conversation_id,
        messageId,
      );

      const { success, conversation_action } = deleteMsg;
      if (!success) showSwal(common.errors.DELETE_MESSAGE_ERROR);
      WS.websocketInstance.sendMessage({
        message: conversation_action,
        type: "user-actions-chat",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const hasAttachment = message_attachment.length > 0;

  const imageAttachments = hasAttachment
    ? message_attachment.filter((attachment) =>
        attachment?.file_type?.includes("image"),
      )
    : [];

  const nonImageAttachments = hasAttachment
    ? message_attachment.filter(
        (attachment) => !attachment?.file_type?.includes("image"),
      )
    : [];

  const FINAL_RESOURCES_URL = process.env.REACT_APP_CHAT_RESOURCES_URL;

  const lightboxImages = imageAttachments.map((attachment) => ({
    src: `${FINAL_RESOURCES_URL}/${attachment.attachment}`,
    type: "image",
  }));

  const hasCaption = Boolean(content && String(content).trim()) || is_deleted;

  // Optimistic flow: true while in-flight, false after API success (until replaced by server list).
  const sendStatusLabel =
    is_optimistic_pending === true
      ? "Sending..."
      : is_optimistic_pending === false
        ? "Sent"
        : null;

  const userDisplayName = userDetails.display_name;
  const isReplyingToOwn =
    reply_to_message_detail?.user?.display_name === userDisplayName;

  const messagesReadsUserNames = (message_reads ?? [])
    ?.filter((messageRead) => messageRead?.user?.display_name)
    .filter((messageRead, index, self) => {
      const userId = messageRead.user.id;
      return index === self.findIndex((msg) => msg.user.id === userId);
    })
    .map((messageRead) =>
      messageRead.user.id === userDetails.id
        ? "You"
        : messageRead.user.display_name,
    );

  const MAX_VISIBLE_SEEN_USERS = 2;
  const visibleUsers = messagesReadsUserNames.slice(0, MAX_VISIBLE_SEEN_USERS);
  const hiddenCount = messagesReadsUserNames.length - MAX_VISIBLE_SEEN_USERS;

  return (
    <>
      <div
        className="message__item-container message__item-container--sender"
        ref={messageContainerRef}
      >
        {reply_to_message_detail && (
          <div className="message__item-container-reply message__item-container-reply--sender">
            <small className="message__item-container-reply--detail message__item-reply-context">
              <span className="message__item-reply-label">Replying to</span>
              <span className="message__item-reply-target">
                {isReplyingToOwn
                  ? "yourself"
                  : reply_to_message_detail.user.display_name}
              </span>
            </small>
            <span className="message__item-container-reply-detail message__item-container-reply-detail--sender">
              {reply_to_message_detail.is_deleted
                ? "DELETED CHAT"
                : trimWord(reply_to_message_detail.content, 32)}
            </span>
          </div>
        )}

        <div
          className={`message__item-message ${
            reply_to_message_detail ? "message__item-message--with-reply" : ""
          }`}
        >
          {!reply_to_message_detail && (
            <span className="message__item-details">
              {/* <span className='message__item-time'>{convertTo12HourFormat(createdAt)}</span> */}
            </span>
          )}
          <div>
            <div className="message__item-text-container-sender" id={chat_id}>
              {is_pinned && (
                <div className="message-pin-container">
                  <div>📌</div>
                </div>
              )}
              <div
                className="message__content-stack message__content-stack--sender"
                onMouseEnter={(e) => {
                  if (e.currentTarget.nextElementSibling) {
                    e.currentTarget.nextElementSibling.style.display = "flex";
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget.nextElementSibling) {
                    e.currentTarget.nextElementSibling.style.display = "none";
                  }
                }}
              >
                {!is_deleted && (
                  <MessageAttachments
                    imageAttachments={imageAttachments}
                    nonImageAttachments={nonImageAttachments}
                    lightboxImages={lightboxImages}
                    onImageClick={handleImageClick}
                    align="sender"
                  />
                )}
                {hasCaption && (
                  <span
                    className={`message__item-text message__item-text--sender message__item-mouse-enter ${
                      is_deleted ? "message__item-deleted" : ""
                    }`}
                  >
                    <MessageComponent message={content} is_deleted={is_deleted} />

                    {!is_deleted && content && /@@Tiger ZERO AI@@/.test(content) && (
                      <div className="ai-command-sent-badge">
                        <i className="fa fa-bolt" />
                        Command sent to Tiger ZERO AI
                      </div>
                    )}
                    {!is_deleted && content && /@@AI@@/.test(content) && (
                      <div className="ai-command-sent-badge ai-command-sent-badge--exclusive">
                        <i className="fa fa-bolt" />
                        AI Command sent
                      </div>
                    )}
                    {sendStatusLabel && (
                      <small
                        className={`message__sending-status${
                          is_optimistic_pending === false
                            ? " message__sending-status--sent"
                            : ""
                        }`}
                      >
                        {sendStatusLabel}
                      </small>
                    )}
                  </span>
                )}
                {!hasCaption && sendStatusLabel && (
                  <small
                    className={`message__sending-status${
                      is_optimistic_pending === false
                        ? " message__sending-status--sent"
                        : ""
                    }`}
                  >
                    {sendStatusLabel}
                  </small>
                )}
              </div>
              <span
                className={`message__item-text message__item-text--sender message__item-time`}
              >
                <p>{convertTo12HourFormat(createdAt)}</p>
              </span>
              {!is_deleted && (
                <div className="message__item-actions" aria-label="Message actions">
                  <MessageActionsMenu
                    alignment="end"
                    onReply={handleOnClickReply}
                    onReplyInThread={handleOnThreadreply}
                    onPin={handleOnClickPin}
                    onInfo={handleViewReactions}
                    onDelete={() => handleDelete(chat_id)}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      toggleShowReactionOptions(chat_id, groupedReaction)
                    }
                    className="reaction-option-emoji-btn message-action-icon-btn"
                    aria-label="Add reaction"
                  >
                    <img src={emojiReaction} className="reaction-option-emoji" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="view-chat-reactions">
          {Object.keys(groupedReaction).map((reaction, index) => (
            <ReactionsIndicator
              reaction={`${reaction}`}
              reaction_details={groupedReaction[reaction]}
              key={index}
            />
          ))}
        </div>
        {thread_replies.length > 0 && (
          <div className="view-thread-replies">
            <small
              className="thread-indicator"
              onClick={(e) => Chat.handleThreadViewMsgId(chat_id)}
            >
              {thread_replies.length} Thread Replies
            </small>
          </div>
        )}
        {messagesReadsUserNames && messagesReadsUserNames.length > 0 && (
          <div className="seen-members-container-sender seen-members-sender">
            <small className="seen-members-text-sender">
              <span className="seen-members-label">Seen by</span>{" "}
              <span
                className="seen-members-inline"
                title={messagesReadsUserNames.join(", ")}
              >
                {visibleUsers.join(", ")}
              </span>
              {hiddenCount > 0 && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="seen-more-btn"
                    onClick={() => setShowSeenModal(true)}
                  >
                    +{hiddenCount} more
                  </button>
                </>
              )}
            </small>
          </div>
        )}
      </div>
      {showSeenModal && (
        <div
          className="seen-modal-overlay"
          onClick={() => setShowSeenModal(false)}
        >
          <div className="seen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="seen-modal-header">
              <h3>Seen By</h3>

              <button type="button" onClick={() => setShowSeenModal(false)}>
                ×
              </button>
            </div>

            <div className="seen-modal-body">
              {messagesReadsUserNames.map((name, index) => (
                <div key={index} className="seen-modal-user">
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MessageSender;
